import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Icon } from "../Icons/Icon";
import { Tooltip } from "../Feedback/Tooltip";
import { describeDisallowedCharacters } from "./disallowedCharacters";

interface NonAsciiWarningProps {
  text: string | undefined;
}

// Match anything outside printable ASCII (U+0020 space through U+007F DEL boundary).
// Smart quotes, accented characters, em-dashes, and other invisible Unicode all fall
// outside that range and trigger the warning — those are the silent-paste hazards
// that break carrier document generation (BOLs, labels) downstream.
const NON_ASCII_REGEX = /[^\u0020-\u007F]/;

function nonAsciiCharacters(text: string | undefined): string[] {
  if (text === undefined) return [];
  return [...new Set([...text].filter((c) => NON_ASCII_REGEX.test(c)))];
}

export function hasNonAsciiCharacters(text: string | undefined): boolean {
  return text !== undefined && NON_ASCII_REGEX.test(text);
}

export function describeNonAsciiCharacters(text: string | undefined): string {
  return describeDisallowedCharacters(nonAsciiCharacters(text));
}

export const NON_ASCII_WARNING_EXPLANATION =
  "These may not print on carrier documents. Retyping the field by hand is the quickest fix.";

export function NonAsciiWarning({ text }: NonAsciiWarningProps) {
  if (!hasNonAsciiCharacters(text)) return undefined;

  return (
    <Tooltip
      content={`${describeNonAsciiCharacters(text)}. ${NON_ASCII_WARNING_EXPLANATION}`}
    >
      <span className="inline-flex cursor-help text-orange">
        <Icon icon={WarningIcon} size="sm" weight="fill" />
      </span>
    </Tooltip>
  );
}
